import { useEffect, useRef, useState } from 'react'
import { useEchoClient } from './echoClient'

export type ContractRealtimeUpdateType =
  | 'balance'
  | 'transaction'
  | 'subscription'

const ECHO_DEBUG =
  import.meta.env.DEV || String(import.meta.env.VITE_ECHO_DEBUG || '') === 'true'

function echoDevLog(...args: unknown[]) {
  if (ECHO_DEBUG) {
    console.info('[Echo contract]', ...args)
  }
}

/**
 * Подписка на private-канал contract.{id}.
 *
 * Имена событий — как в broadcastAs() на сервере: contract.balance.changed,
 * contract.transaction.created|approved|rejected и contract.subscription.consumed
 * (класс события называется LessonSubscriptionConsumed, но вещает он под
 * contract.*, потому что канал — договора, а не урока).
 */
export function useContractRealtime(
  contractId: number | null | undefined,
  onUpdate: (type: ContractRealtimeUpdateType) => void
) {
  const [isConnected, setIsConnected] = useState(false)
  const onUpdateRef = useRef(onUpdate)
  onUpdateRef.current = onUpdate

  // Клиент — зависимость эффекта: после пересоздания Echo подписка иначе
  // осталась бы на отключённом сокете.
  const echo = useEchoClient()

  useEffect(() => {
    if (!Number.isFinite(contractId)) {
      setIsConnected(false)
      return
    }

    const channelName = `contract.${contractId}`

    setIsConnected(false)

    const channel = echo.private(channelName)

    type ChannelWithLifecycle = typeof channel & {
      subscribed?: (cb: () => void) => typeof channel
      error?: (cb: (err: unknown) => void) => typeof channel
    }
    const ch = channel as ChannelWithLifecycle

    if (typeof ch.subscribed === 'function') {
      ch.subscribed(() => {
        setIsConnected(true)
        echoDevLog(`Подписка на private-${channelName} подтверждена`)
      })
    } else {
      setIsConnected(true)
    }

    if (typeof ch.error === 'function') {
      ch.error((err) => {
        setIsConnected(false)
        console.error('[Echo] Ошибка подписки на канал', channelName, err)
      })
    }

    const refreshBalance = () => onUpdateRef.current('balance')
    const refreshTransaction = () => onUpdateRef.current('transaction')
    const refreshSubscription = () => onUpdateRef.current('subscription')

    channel
      .listen('.contract.balance.changed', refreshBalance)
      .listen('.contract.transaction.created', refreshTransaction)
      .listen('.contract.transaction.approved', refreshTransaction)
      .listen('.contract.transaction.rejected', refreshTransaction)
      // Не '.lesson.subscription.consumed': такого имени сервер не шлёт, и
      // остаток абонемента не обновлялся до перезагрузки страницы договора
      .listen('.contract.subscription.consumed', refreshSubscription)

    echoDevLog(`Подписка на private-${channelName}`)

    return () => {
      try {
        echo.leave(channelName)
      } catch {
        // ignore
      }
      setIsConnected(false)
    }
  }, [contractId, echo])

  return { isConnected }
}
